import { defineStore } from 'pinia'
import type { SettingsState, Locale, Theme, Riwaya, AudioSettings, UserPreferences, PlayerFontSize, TassmiEndFeedback } from '../types'

const SETTINGS_KEY = 'quran-settings'

// Default settings
const defaultAudioSettings: AudioSettings = {
  autoPlayNextAya: false,
  playbackSpeed: 1.0,
  volume: 1.0,
}

const defaultPreferences: UserPreferences = {
  showTranslation: true,
  showTransliteration: false,
  playerFontSize: 'medium',
  tassmi3EndFeedback: 'both',
  tassmi3Reciter: 263,
}

export const useSettingsStore = defineStore('settings', {
  state: (): SettingsState => {
    // Load from localStorage if exists
    const saved = localStorage.getItem(SETTINGS_KEY)
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        // Older saved settings won't have `riwaya`; normalize to null so the
        // onboarding gate treats it as "not picked yet".
        if (parsed.riwaya === undefined) parsed.riwaya = null
        return parsed
      } catch (e) {
        console.error('Failed to parse settings:', e)
      }
    }

    // Default state
    return {
      locale: 'en',
      theme: 'light',
      riwaya: null,
      audioSettings: { ...defaultAudioSettings },
      preferences: { ...defaultPreferences },
    }
  },

  getters: {
    /**
     * Get current locale
     */
    currentLocale: (state): Locale => state.locale,

    /**
     * Get current theme
     */
    currentTheme: (state): Theme => state.theme,

    /**
     * Check if dark mode is enabled
     */
    isDarkMode: (state): boolean => state.theme === 'dark',

    /**
     * Check if Arabic is selected
     */
    isArabic: (state): boolean => state.locale === 'ar',

    /**
     * Get current riwaya (recitation tradition), or null if not picked yet
     */
    currentRiwaya: (state): Riwaya | null => state.riwaya,
  },

  actions: {
    /**
     * Set locale
     */
    setLocale(locale: Locale) {
      this.locale = locale
      this.persist()
    },

    /**
     * Toggle locale
     */
    toggleLocale() {
      this.locale = this.locale === 'ar' ? 'en' : 'ar'
      this.persist()
    },

    /**
     * Set theme
     */
    setTheme(theme: Theme) {
      this.theme = theme
      this.persist()
    },

    /**
     * Set riwaya (recitation tradition)
     */
    setRiwaya(riwaya: Riwaya) {
      this.riwaya = riwaya
      this.persist()
    },

    /**
     * Toggle theme
     */
    toggleTheme() {
      this.theme = this.theme === 'light' ? 'dark' : 'light'
      this.persist()
    },

    /**
     * Update audio settings
     */
    updateAudioSettings(settings: Partial<AudioSettings>) {
      this.audioSettings = {
        ...this.audioSettings,
        ...settings,
      }
      this.persist()
    },

    /**
     * Update preferences
     */
    updatePreferences(preferences: Partial<UserPreferences>) {
      this.preferences = {
        ...this.preferences,
        ...preferences,
      }
      this.persist()
    },

    /**
     * Set player font size
     */
    setPlayerFontSize(size: PlayerFontSize) {
      this.preferences.playerFontSize = size
      this.persist()
    },

    /**
     * Set tassmi' end-of-session feedback type
     */
    setTassmi3EndFeedback(type: TassmiEndFeedback) {
      this.preferences.tassmi3EndFeedback = type
      this.persist()
    },

    /**
     * Set Warsh reciter for Mushaf audio playback
     */
    setTassmi3Reciter(id: number) {
      this.preferences.tassmi3Reciter = id
      this.persist()
    },

    /**
     * Reset to defaults
     */
    resetToDefaults() {
      this.locale = 'en'
      this.theme = 'light'
      this.riwaya = null
      this.audioSettings = { ...defaultAudioSettings }
      this.preferences = { ...defaultPreferences }
      this.persist()
    },

    /**
     * Persist settings to localStorage
     */
    persist() {
      try {
        localStorage.setItem(SETTINGS_KEY, JSON.stringify(this.$state))
      } catch (e) {
        console.error('Failed to save settings:', e)
      }
    },

    /**
     * Load settings from localStorage
     */
    loadFromStorage() {
      const saved = localStorage.getItem(SETTINGS_KEY)
      if (saved) {
        try {
          const settings = JSON.parse(saved)
          this.$state = settings
        } catch (e) {
          console.error('Failed to load settings:', e)
        }
      }
    },
  },
})
